import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;

const EmailChangeConfirmation: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid email change link. No token provided.');
      return;
    }

    // Confirm the email change
    // Add a small delay to ensure proper loading
    const timer = setTimeout(() => {
      const confirmEmailChange = async () => {
        if (isProcessing) return; // Prevent multiple calls
        
        setIsProcessing(true);
        try {
        const response = await axios.post(`${API_BASE_URL}/email/confirm-email-change`, {
          token
        });

        if (response.data.success) {
          setStatus('success');
          setMessage('Email address changed successfully! Please log in again with your new email address.');
          
          // Clear stored auth and redirect to login after 3 seconds
          localStorage.removeItem('fragrance-organizer-token');
          localStorage.removeItem('fragrance-organizer-user');
          
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Email change confirmation failed');
        }
      } catch (error: any) {
        console.error('Email change confirmation error:', error);
        
        if (error.response?.status === 400) {
          setStatus('expired');
          setMessage(error.response.data.message || 'Email change link has expired or is invalid');
        } else {
          setStatus('error');
          setMessage('Email change confirmation failed. Please try again.');
        }
        } finally {
          setIsProcessing(false);
        }
      };

      confirmEmailChange();
    }, 100); // 100ms delay to ensure proper initialization

    return () => clearTimeout(timer);
  }, [location.search, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
      case 'expired':
        return '❌';
      default:
        return '📧';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
      case 'expired':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-blue-600 dark:text-blue-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8"
      >
        <div className="text-center">
          <div className="text-6xl mb-4">
            {status === 'loading' ? (
              <LoadingSpinner size="lg" className="mx-auto" />
            ) : (
              getStatusIcon()
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {status === 'loading' && 'Confirming Email Change...'}
            {status === 'success' && 'Email Changed!'}
            {status === 'error' && 'Confirmation Failed'}
            {status === 'expired' && 'Link Expired'}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`mb-6 ${getStatusColor()}`}
          >
            <p className="text-sm">{message}</p>
          </motion.div>

          <div className="space-y-4">
            {status === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
              >
                <p className="text-green-600 dark:text-green-400 text-sm">
                  🎉 Your email address has been updated! You'll be redirected to the login page shortly.
                </p>
              </motion.div>
            )}

            {(status === 'error' || status === 'expired') && (
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                >
                  Continue to Dashboard
                </Button>
              </div>
            )}

            {status === 'loading' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-blue-600 dark:text-blue-400 text-sm">
                  Please wait while we confirm your email change...
                </p>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="w-full text-sm"
              >
                ← Back to Home
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailChangeConfirmation;
